import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, FileText, Loader2, Send, QrCode, LogIn } from "lucide-react";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { ProfileView } from "@/components/company/ProfileView";
import { FullLoader } from "@/components/auth/RequireRole";
import { useI18n } from "@/lib/i18n/i18n";
import { fullName, DAY_LABELS } from "@/lib/demo/utils";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ORIGIN_LABELS } from "@/lib/i18n/dictInteracciones";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

/**
 * One interaction model, two participant-side origins:
 *  - standToken present  -> Option B (candidate_scanned_company), always with explicit confirmation
 *  - no standToken       -> post-event online submission (post_event_online), if the edition allows it
 */
const ApplyCard = ({ company, standToken }) => {
  const { user, participant: p, settings } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [existing, setExisting] = useState(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isParticipant = user?.role === "participant";
  const viaQr = !!standToken;
  const enabled = viaQr ? settings.optionB : settings.postEventOpen;

  useEffect(() => {
    if (!isParticipant) { setExisting(null); return; }
    api.get(`/companies/${company.id}/my-interaction`).then((r) => {
      setExisting(r.data.interaction);
      if (!r.data.interaction && viaQr) setConfirmOpen(true);
    }).catch(() => setExisting(null));
  }, [company.id, isParticipant, viaQr]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/apply`, viaQr ? { via: "qr", k: standToken } : { via: "web" });
      setExisting(data.interaction);
      setJustSent(data.status === "received");
      setConfirmOpen(false);
      toast.success(t("CV compartido con éxito con {company}.", { company: company.name }));
    } catch (e) {
      toast.error(t(apiError(e).message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm lg:sticky lg:top-24" data-testid="company-apply-card">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Comparte tu currículum")}</p>
      {viaQr && <p className="mt-3 flex items-center gap-2 rounded-xl bg-upv-light px-3 py-2 text-xs font-semibold text-upv" data-testid="company-apply-stand-banner"><QrCode className="h-4 w-4" /> {t("Has escaneado el QR del stand {stand}", { stand: company.stand })}</p>}
      {!isParticipant ? (
        <>
          <p className="mt-4 text-sm text-slate-600">{t(user ? "Solo los participantes registrados pueden enviar su currículum." : "Inicia sesión con tu cuenta de participante para enviar tu currículum.")}</p>
          {!user && (
            <Link to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="company-apply-login-button">
              <LogIn className="h-4 w-4" /> {t("INICIAR SESIÓN")}
            </Link>
          )}
          {!user && <Link to="/registro" className="mt-3 block text-center text-sm font-semibold text-upv hover:underline">{t("¿Aún no tienes credencial? Regístrate")}</Link>}
        </>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
            <FileText className="h-5 w-5 shrink-0 text-upv" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{p ? fullName(p) : ""}</p>
              <p className="truncate font-mono text-xs text-slate-500">{p?.cvFileName}</p>
            </div>
          </div>
          {existing === undefined ? <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-slate-400" /> : existing ? (
            <div className={`mt-5 rounded-2xl p-5 ${justSent ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-800"}`} data-testid="company-apply-done">
              <p className="flex items-center gap-2 font-display text-lg font-bold"><CheckCircle2 className="h-5 w-5 shrink-0" /> {justSent ? t("CV compartido con éxito con {company}.", { company: company.name }) : t("Ya compartiste tu currículum con {company}", { company: company.name })}</p>
              <p className={`mt-1 text-sm ${justSent ? "text-white/80" : "text-emerald-700"}`} data-testid="company-apply-done-origin">
                {DAY_LABELS[existing.day] ? t(DAY_LABELS[existing.day]) : existing.date} · {existing.time} · {t(ORIGIN_LABELS[existing.origin] || existing.origin)}
              </p>
            </div>
          ) : !enabled ? (
            <p className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600" data-testid="company-apply-disabled">{t(viaQr ? "La lectura del QR de empresa está desactivada en esta edición" : "El envío online de CV estará disponible tras el Foro.")}</p>
          ) : (
            <>
              <p className="mt-4 text-sm text-slate-600">{t("Se compartirá tu perfil profesional y currículum con {company}.", { company: company.name })}</p>
              <button onClick={() => setConfirmOpen(true)} disabled={sending} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="company-apply-button">
                <Send className="h-4 w-4" /> {t("ENVIAR MI CURRÍCULUM")}
              </button>
            </>
          )}
        </>
      )}
      <p className="mt-4 text-xs text-slate-400">{t("También puedes mostrar tu QR en el stand {stand}.", { stand: company.stand })}</p>
      <AlertDialog open={confirmOpen && isParticipant && enabled && !existing} onOpenChange={(o) => !sending && setConfirmOpen(o)}>
        <AlertDialogContent className="rounded-[28px]" data-testid="company-apply-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold" data-testid="company-apply-confirm-title">{t("¿Quieres compartir tu perfil y CV con {company}?", { company: company.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t("{company} podrá ver tu perfil profesional y descargar tu currículum.", { company: company.name })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="company-apply-confirm-cancel">{t("Cancelar")}</AlertDialogCancel>
            <button onClick={send} disabled={sending} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-upv px-5 text-sm font-bold text-white hover:bg-upv-dark disabled:opacity-70" data-testid="company-apply-confirm-share">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Compartir CV")}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function EmpresaPerfil() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const [company, setCompany] = useState(undefined);
  useEffect(() => {
    setCompany(undefined);
    api.get(`/companies/${id}`).then((r) => setCompany(r.data)).catch(() => setCompany(null));
  }, [id]);
  if (company === undefined) return <FullLoader />;
  if (!company) return <Navigate to="/empresas" replace />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-profile-page">
      <SiteHeader />
      <ProfileView
        company={company}
        aside={<ApplyCard company={company} standToken={params.get("k")} />}
        topLeft={<Link to="/empresas" className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold backdrop-blur transition-colors hover:bg-white lg:left-10" data-testid="company-back-link"><ArrowLeft className="h-3.5 w-3.5" /> {t("Todas las empresas")}</Link>}
      />
      <PageFooter />
    </div>
  );
}
